import { soundVolume } from "../constants";

// Geluid op het dashboard.
//
// Alles wat je hoort wordt hier ter plekke gemaakt met de WebAudio-api: geen
// mp3's, dus geen extra bestanden die mee moeten in de build naar de
// Raspberry Pi en geen codec die op het ene apparaat wel en op het andere
// niet werkt. Een paar oscillatoren en een ruisbuffer zijn genoeg voor wat we
// nodig hebben, en het kost vrijwel niets: het geluid loopt buiten het
// tekenen om, dus de animaties worden er niet trager van.
//
// Browsers laten geen geluid toe voordat iemand de pagina heeft aangeraakt.
// Op een tv raakt niemand iets aan, dus er zijn twee wegen:
//   1. de eerste klik, toets of aanraking zet het geluid aan (listenForUnlock)
//   2. start Chromium op de Pi met --autoplay-policy=no-user-gesture-required,
//      dan hoeft er niets aangeraakt te worden. Zie PRESTATIES.md.
//
// Uitzetten kan per scherm: ?sound=off achter de URL, of de knop in het
// animatielab. Die keuze blijft staan, ook na herstarten.

const storeKey = "tdSound";

let ctx = null;
let master = null;
let noiseBuffer = null;
let on = null;
const listeners = new Set();

// Onthouden per scherm. Ook de geluidsset gebruikt dit (zie cues.js), want
// dat is dezelfde soort keuze: iets wat je één keer instelt op een tv waar je
// verder niet bij kunt.
export function readSetting(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Kiosk-modus zonder opslag: dan geldt de keuze alleen deze sessie.
    return null;
  }
}

export function writeSetting(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Niets aan te doen.
  }
}

// Een keuze uit de URL, die meteen onthouden wordt: ?sound=off zet het geluid
// uit, ?soundset=arcade kiest een set. Op een tv typ je niet makkelijk een
// adres, dus dat wil je één keer hoeven doen.
export function readUrlSetting(param, allowed, key) {
  try {
    const asked = new URLSearchParams(window.location.search).get(param);
    if (!asked || !allowed.includes(asked)) return null;
    writeSetting(key, asked);
    return asked;
  } catch {
    return null;
  }
}

function resolve() {
  const asked = readUrlSetting("sound", ["on", "off"], storeKey);
  const saved = asked || readSetting(storeKey);
  if (saved === "on" || saved === "off") return saved === "on";
  return true;
}

export function soundOn() {
  if (on === null) on = resolve();
  return on;
}

export function setSoundOn(next, { remember = true } = {}) {
  on = Boolean(next);
  if (remember) writeSetting(storeKey, on ? "on" : "off");
  if (on) resume();
  listeners.forEach((listener) => listener(on));
}

// Voor het paneel in het lab, dat mee moet veranderen als de knop omgaat.
export function onSoundChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// De audiocontext komt er pas bij het eerste geluid: hem bij het laden al
// aanmaken levert in de meeste browsers een geblokkeerde context op die we
// daarna alsnog moeten wekken.
function context() {
  if (ctx) return ctx;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;

  ctx = new Ctx();
  master = ctx.createGain();
  master.gain.value = soundVolume;

  // Een begrenzer aan het eind. Bij een verkoop lopen er dingen over elkaar
  // heen (de aankondiging met de balk, drie buien confetti), en wat daarboven
  // uitkomt knipt de browser er hard af: dat klikt en kraakt. Deze vangt
  // alleen de pieken af; alles eronder gaat ongemoeid door, dus het geluid
  // wordt er niet plat van.
  const limiter = ctx.createDynamicsCompressor();
  limiter.threshold.value = -3;
  limiter.knee.value = 0;
  limiter.ratio.value = 20;
  limiter.attack.value = 0.002;
  limiter.release.value = 0.12;

  master.connect(limiter);
  limiter.connect(ctx.destination);
  return ctx;
}

// Het geluid dat nu opgebouwd wordt mag harder. De drie die samen het moment
// maken (het seintje, de aanloop, de aankondiging) gaan hier doorheen; zie
// accented in cues.js. Het staat op een variabele en niet in elke toon apart,
// want een geluid wordt in één keer klaargezet: alles wat tussen het zetten
// en het terugzetten hieronder gepland wordt, hoort bij hetzelfde geluid.
let accent = 1;

export function withAccent(factor, run) {
  accent = factor;
  try {
    run();
  } finally {
    accent = 1;
  }
}

function resume() {
  const audio = context();
  if (!audio) return false;
  if (audio.state === "suspended") audio.resume().catch(() => {});
  return audio.state === "running";
}

// Of er op dit moment ook echt iets te horen zou zijn. Het lab laat dit zien,
// zodat "ik hoor niets" niet hoeft te betekenen dat je gaat zoeken in code
// die het gewoon doet.
export function audioReady() {
  if (!soundOn()) return false;
  return Boolean(ctx && ctx.state === "running");
}

// Wachten op de eerste aanraking. Geeft een opruimfunctie terug.
export function listenForUnlock() {
  if (typeof window === "undefined") return () => {};

  // Meteen proberen: met de kioskvlag hierboven mag het al.
  resume();

  const wake = () => {
    if (resume()) stop();
  };
  const events = ["pointerdown", "keydown", "touchstart"];
  const stop = () => events.forEach((name) => window.removeEventListener(name, wake));

  events.forEach((name) => window.addEventListener(name, wake, { passive: true }));
  return stop;
}

// Ruis voor de doffe klappen (vuurwerk, de klap onder de aankondiging). Eén
// seconde is genoeg; langer hebben we nergens nodig.
function noise() {
  const audio = context();
  if (!audio) return null;
  if (noiseBuffer) return noiseBuffer;

  const frames = audio.sampleRate;
  noiseBuffer = audio.createBuffer(1, frames, audio.sampleRate);
  const data = noiseBuffer.getChannelData(0);
  for (let i = 0; i < frames; i += 1) data[i] = Math.random() * 2 - 1;
  return noiseBuffer;
}

// Of we nu iets mogen laten horen. Alle bouwstenen hieronder beginnen
// hiermee, zodat een uitgezet of nog geblokkeerd geluid nergens anders
// afgevangen hoeft te worden.
function open() {
  if (!soundOn()) return null;
  const audio = context();
  if (!audio || audio.state !== "running") {
    resume();
    return null;
  }
  return audio;
}

// Eén toon. freq -> to glijdt hij naartoe; zonder to blijft hij staan. De
// uitdoving is exponentieel, want lineair klinkt als een afgeknipte piep.
export function tone({
  at = 0,
  freq,
  to = null,
  type = "sine",
  duration = 0.3,
  gain = 0.3,
  attack = 0.006,
}) {
  const audio = open();
  if (!audio) return;

  const start = audio.currentTime + at;
  const osc = audio.createOscillator();
  const level = audio.createGain();

  osc.type = type;
  osc.frequency.setValueAtTime(freq, start);
  if (to) osc.frequency.exponentialRampToValueAtTime(Math.max(1, to), start + duration);

  level.gain.setValueAtTime(0.0001, start);
  level.gain.exponentialRampToValueAtTime(Math.max(0.0001, gain * accent), start + attack);
  level.gain.exponentialRampToValueAtTime(0.0001, start + duration);

  osc.connect(level);
  level.connect(master);
  osc.start(start);
  osc.stop(start + duration + 0.05);
}

// Een veeg of een klap: ruis door een filter dat van from naar to zakt.
export function sweep({
  at = 0,
  duration = 0.4,
  gain = 0.2,
  from = 6000,
  to = 300,
  type = "lowpass",
  q = 1,
}) {
  const audio = open();
  if (!audio) return;

  const buffer = noise();
  if (!buffer) return;

  const start = audio.currentTime + at;
  const source = audio.createBufferSource();
  const filter = audio.createBiquadFilter();
  const level = audio.createGain();

  source.buffer = buffer;
  source.loop = true;

  filter.type = type;
  filter.Q.value = q;
  filter.frequency.setValueAtTime(from, start);
  filter.frequency.exponentialRampToValueAtTime(Math.max(40, to), start + duration);

  level.gain.setValueAtTime(0.0001, start);
  level.gain.exponentialRampToValueAtTime(Math.max(0.0001, gain * accent), start + 0.02);
  level.gain.exponentialRampToValueAtTime(0.0001, start + duration);

  source.connect(filter);
  filter.connect(level);
  level.connect(master);
  source.start(start);
  source.stop(start + duration + 0.05);
}

// Wat er op dit moment uit een bestand speelt. De tonen hierboven duren een
// fractie van een seconde en zijn voorbij voor je ze zou willen stoppen; een
// opname kan seconden duren, en het feestje duurt een kwart minuut. Die wil
// je kunnen afbreken als iemand Escape drukt of als de tv uitgaat.
const playing = new Set();

// Een eigen geluidsbestand afspelen, als er een ligt. Het ophalen en
// bewaren gebeurt in samples.js; hier wordt hij alleen aangesloten.
//
// fadeIn/fadeOut zijn in seconden en horen bij de langere opnames: een nummer
// dat op volle sterkte begint klinkt als een knop die omgaat, en een dat
// halverwege een maat ophoudt klinkt als een storing. Met een paar seconden
// ervoor en erna komt het op gang en ebt het weg. Kort geluid heeft dit niet
// nodig - dat is zelf al een aanzet - dus standaard staat het uit.
export function playBuffer(buffer, { at = 0, gain = 1, fadeIn = 0, fadeOut = 0 } = {}) {
  const audio = open();
  if (!audio || !buffer) return false;

  const source = audio.createBufferSource();
  const level = audio.createGain();

  const start = audio.currentTime + at;
  const end = start + buffer.duration;

  source.buffer = buffer;

  // Exponentieel, net als de tonen hierboven: lineair naar nul klinkt als
  // een afgeknepen geluid in plaats van als iets dat wegzakt. Daarom ook
  // nooit helemaal naar 0 - dat mag niet in een exponentiële ramp.
  // De infade begint op -40 dB en niet op nul. Een exponentiële ramp loopt
  // gelijkmatig in decibel, en vanaf -80 dB is de eerste helft zo zacht dat
  // je hem niet hoort: dan voelt drie seconden als anderhalf.
  if (fadeIn > 0) {
    level.gain.setValueAtTime(Math.max(0.0001, gain * 0.01), start);
    level.gain.exponentialRampToValueAtTime(Math.max(0.0001, gain), start + fadeIn);
  } else {
    level.gain.setValueAtTime(gain, start);
  }

  // De uitfade begint aan het eind van het bestand, en nooit voor de infade
  // klaar is: bij een opname die korter is dan de twee samen blijft er dan
  // alsnog iets over in plaats van niets.
  if (fadeOut > 0) {
    const from = Math.max(start + fadeIn, end - fadeOut);
    level.gain.setValueAtTime(Math.max(0.0001, gain), from);
    level.gain.exponentialRampToValueAtTime(0.0001, end);
  }

  source.connect(level);
  level.connect(master);
  source.start(start);

  const entry = { source, level };
  playing.add(entry);
  source.onended = () => playing.delete(entry);
  return true;
}

// Alles wat uit een bestand speelt afbreken. Niet ineens: een geluid dat
// midden in een golf wordt afgekapt klikt, en op een tv hoor je dat door de
// hele ruimte. Een vijfde seconde is genoeg om dat weg te halen en kort
// genoeg om nog steeds "meteen" te heten.
export function stopSamples({ fade = 0.2 } = {}) {
  if (!ctx || playing.size === 0) return;

  const now = ctx.currentTime;
  playing.forEach(({ source, level }) => {
    try {
      level.gain.cancelScheduledValues(now);
      level.gain.setValueAtTime(Math.max(0.0001, level.gain.value), now);
      level.gain.exponentialRampToValueAtTime(0.0001, now + fade);
      source.stop(now + fade + 0.02);
    } catch {
      // Was al afgelopen. Niets aan te doen, en niets aan de hand.
    }
  });
  playing.clear();
}

// Een opgehaald bestand omzetten naar iets wat we kunnen afspelen. Geeft null
// als dat niet lukt: geen audio in deze browser, of geen bestand dat hij
// snapt. Dat laatste is ook wat er gebeurt als het bestand er niet is - een
// webserver geeft dan meestal de pagina zelf terug, en dat is geen geluid.
export async function decodeAudio(data) {
  const audio = context();
  if (!audio) return null;
  try {
    return await audio.decodeAudioData(data);
  } catch {
    return null;
  }
}

// Een klokje: twee tonen op elkaar, de bovenste zachter. Klinkt voller dan
// één sinus en blijft klein genoeg om er tien achter elkaar te spelen.
export function bell({ at = 0, freq, duration = 0.9, gain = 0.22 }) {
  tone({ at, freq, type: "sine", duration, gain });
  tone({ at, freq: freq * 2.01, type: "sine", duration: duration * 0.55, gain: gain * 0.35 });
}
